import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import instagramIcon from '../../assets/icons/icon_instagram.png';
import youtubeIcon from '../../assets/icons/icon_youtube.png';
import { Spacer } from '../../common/components/Spacer';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import {
  LayoutFooter as LayoutFooterWrapper,
  LayoutFooterAside,
  LayoutFooterAsideContent,
  LayoutFooterCompanyContentItem,
  LayoutFooterCompanyContentList,
  LayoutFooterContact,
  LayoutFooterContactContent,
  LayoutFooterContactItem,
  LayoutFooterContactList,
  LayoutFooterFamilySiteContent,
  LayoutFooterFamilySiteItem,
  LayoutFooterFamilySiteSection,
  LayoutFooterHealthhubLogo,
  LayoutFooterMenu,
  LayoutFooterMenuContent,
  LayoutFooterMenuContentItem,
  LayoutFooterMenuHeading,
  LayoutFooterMenuInternalLink,
  LayoutFooterMenuLink,
  LayoutFooterMenuSection,
  LayoutFooterMenuSection2,
  LayoutFooterMenuSection3,
  LayoutFooterMenuSnsIcons,
  LayoutFooterMobileAddressTitle,
  LayoutFooterPrivacyPolicyLink,
  SnsIcon,
  SnsLink,
  SnSWrapper,
} from './components';

export const LayoutFooter = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'layout.footer' });

  const windowSize = useContext(WindowSizeContext);
  const isMobile = windowSize === WindowSize.MOBILE;

  return (
    <LayoutFooterWrapper>
      <LayoutFooterContact>
        <LayoutFooterContactContent>
          <LayoutFooterContactList>
            {!isMobile && (
              <LayoutFooterContactItem>
                {t('contact.고객지원')}
              </LayoutFooterContactItem>
            )}
            <LayoutFooterContactItem>
              <LayoutFooterMenuInternalLink to="/faq">
                {t('contact.자주묻는질문')}
              </LayoutFooterMenuInternalLink>
            </LayoutFooterContactItem>
            <LayoutFooterContactItem>
              <LayoutFooterMenuLink href="tel:070-4486-8733" target="_blank">
                {t('contact.고객센터')}
              </LayoutFooterMenuLink>
            </LayoutFooterContactItem>
            <LayoutFooterContactItem>
              <LayoutFooterMenuLink
                href="mailto:inquiry@healthhub.kr"
                target="_blank">
                {t('contact.이메일')}
              </LayoutFooterMenuLink>
            </LayoutFooterContactItem>
            <LayoutFooterContactItem>
              <LayoutFooterMenuLink
                href="https://healthhub.kr/main/?mc_code=1611"
                target="_blank">
                {t('contact.병원도입문의')}
              </LayoutFooterMenuLink>
            </LayoutFooterContactItem>
          </LayoutFooterContactList>
        </LayoutFooterContactContent>
      </LayoutFooterContact>
      <LayoutFooterAside>
        <LayoutFooterAsideContent>
          <LayoutFooterMenu>
            <LayoutFooterMenuSection>
              <LayoutFooterHealthhubLogo />
            </LayoutFooterMenuSection>
            <Spacer height="15px" />
            <SnSWrapper>
              <LayoutFooterFamilySiteSection>
                <LayoutFooterMenuHeading>
                  <strong>{t('familySite.familySite')}</strong>
                </LayoutFooterMenuHeading>
                <LayoutFooterFamilySiteContent>
                  <LayoutFooterFamilySiteItem>
                    <LayoutFooterMenuLink
                      href="https://www.healthhub.kr/"
                      target="_blank">
                      {t('familySite.healthhub')}
                    </LayoutFooterMenuLink>
                  </LayoutFooterFamilySiteItem>
                  <LayoutFooterFamilySiteItem>
                    <LayoutFooterMenuLink
                      href="https://dicomlink.kr/home/"
                      target="_blank">
                      {t('familySite.hpacs')}
                    </LayoutFooterMenuLink>
                  </LayoutFooterFamilySiteItem>
                  <LayoutFooterFamilySiteItem>
                    <LayoutFooterMenuLink
                      href="https://htelerad.com/"
                      target="_blank">
                      {t('familySite.hTeleRad')}
                    </LayoutFooterMenuLink>
                  </LayoutFooterFamilySiteItem>
                  <LayoutFooterFamilySiteItem>
                    <LayoutFooterMenuInternalLink to="/">
                      {t('familySite.hscan')}
                    </LayoutFooterMenuInternalLink>
                  </LayoutFooterFamilySiteItem>
                  <LayoutFooterFamilySiteItem>
                    <LayoutFooterMenuLink
                      href="http://market.dicomlink.kr/"
                      target="_blank">
                      {t('familySite.hAiHub')}
                    </LayoutFooterMenuLink>
                  </LayoutFooterFamilySiteItem>
                  <LayoutFooterFamilySiteItem>
                    <LayoutFooterMenuLink
                      href="https://www.boneage.io/"
                      target="_blank">
                      {t('familySite.boneage')}
                    </LayoutFooterMenuLink>
                  </LayoutFooterFamilySiteItem>
                </LayoutFooterFamilySiteContent>
              </LayoutFooterFamilySiteSection>
              <LayoutFooterMenuSnsIcons>
                <SnsLink
                  href="https://www.instagram.com/healthhub_kr/"
                  target="_blank">
                  <SnsIcon width="20px" height="20px" src={instagramIcon} />
                </SnsLink>
                <SnsLink
                  href="https://www.youtube.com/@healthhubinc.3455"
                  target="_blank">
                  <SnsIcon width="20px" height="20px" src={youtubeIcon} />
                </SnsLink>
              </LayoutFooterMenuSnsIcons>
            </SnSWrapper>
            <Spacer height="30px" />
            <LayoutFooterMenuSection2>
              <LayoutFooterMenuSection>
                <LayoutFooterMenuContent>
                  <LayoutFooterMenuContentItem>
                    <LayoutFooterPrivacyPolicyLink to="/terms-v2/privacy-policy">
                      {t('terms.privacyPolicy')}
                    </LayoutFooterPrivacyPolicyLink>
                  </LayoutFooterMenuContentItem>
                  <LayoutFooterMenuContentItem>
                    <LayoutFooterMenuInternalLink to="/terms-v2/terms-of-service">
                      {t('terms.termsOfUse')}
                    </LayoutFooterMenuInternalLink>
                  </LayoutFooterMenuContentItem>
                </LayoutFooterMenuContent>
              </LayoutFooterMenuSection>
            </LayoutFooterMenuSection2>
            <Spacer height="30px" />
            <LayoutFooterMenuSection3>
              <LayoutFooterCompanyContentList>
                <LayoutFooterCompanyContentItem>
                  {t('cooperation.companyName')}
                </LayoutFooterCompanyContentItem>
                <LayoutFooterCompanyContentItem>
                  <Trans
                    t={t}
                    i18nKey="cooperation.address"
                    components={
                      isMobile
                        ? [<LayoutFooterMobileAddressTitle />]
                        : [<strong />]
                    }
                  />
                </LayoutFooterCompanyContentItem>
                <LayoutFooterCompanyContentItem>
                  <Trans
                    t={t}
                    i18nKey="cooperation.telecom"
                    components={[<strong />]}
                  />
                </LayoutFooterCompanyContentItem>
                <LayoutFooterCompanyContentItem>
                  <Trans
                    t={t}
                    i18nKey="cooperation.email"
                    components={[<strong />]}
                  />
                </LayoutFooterCompanyContentItem>
                <LayoutFooterCompanyContentItem>
                  {t('copyright')}
                </LayoutFooterCompanyContentItem>
              </LayoutFooterCompanyContentList>
            </LayoutFooterMenuSection3>
          </LayoutFooterMenu>
        </LayoutFooterAsideContent>
      </LayoutFooterAside>
    </LayoutFooterWrapper>
  );
};
